import { Layout, Menu } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountStore } from 'store/accountStore';
import { AccountInterface } from 'type/accountType';

import {
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  VIDEO_MENU_ITEMS,
} from 'constants/menu';
import './Sidebar.css';

export interface SidebarProps {
  currentPath: string;
  defaultOpenKeys: string[];
  children: React.ReactNode;
}

const { Content, Footer, Sider } = Layout;

const Sidebar: React.FC<SidebarProps> = observer(({ children, currentPath, defaultOpenKeys }) => {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [, setUser] = useState<AccountInterface | null>(null);
  const menus = accountStore.getMenus();
  const contentMenuList = useMemo(
    () => CONTENT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const contractMenuList = useMemo(
    () => CONTRACT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const revenueMenuList = useMemo(
    () => REVENUE_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const videoMenuList = useMemo(() => VIDEO_MENU_ITEMS, [menus]) || [];

  useEffect(() => {
    const user = accountStore.getAccount();
    if (user) {
      setImageUrl(user.platform.logo);
      setUser(user);
    }
  }, []);

  return (
    <Layout className="main-layout" hasSider>
      <Sider className="sider" width={200}>
        <button
          className="logo-button"
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src={imageUrl} className="logo-image" alt="main_logo" />
        </button>
        <div className="menu-header"></div>
        <div className="content-menu-header">
          <div>Content</div>
        </div>
        <Menu
          className="menu"
          theme="dark"
          mode="inline"
          items={contentMenuList}
          selectedKeys={[currentPath]}
          defaultOpenKeys={defaultOpenKeys}
        />
        <>
          {contractMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Contract</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={contractMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {revenueMenuList.length > 0 && (
            <div>
              <div className="revenue-menu-header">
                <div>Revenue</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={revenueMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {videoMenuList.length > 0 && (
            <div>
              <div className="video-menu-header">
                <div>Video</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={videoMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
        </>
      </Sider>
      <Layout className="layout-content">
        <Content className="content">{children}</Content>
        <Footer className="footer">ODKMedia, Inc</Footer>
      </Layout>
    </Layout>
  );
});

export default React.memo(Sidebar);
